// EntityPicker — generic typeahead with "+ Add" inline create.
//
// Two callers right now:
//   1. Company picker in the deal modal     (renderRow shows the logo)
//   2. Contact-at-company picker            (renderRow shows initials)
//
// Required props:
//   - value:          the currently-selected entity ({ id, ... }) or null
//   - placeholder:    input placeholder string
//   - onSelect(item): called when the user picks an existing row
//   - onCreate(name): called when the user clicks "+ Add 'X'".
//                     Receives the typed name; the caller does whatever
//                     create call it needs and returns the created object.
//   - search(q):      async function returning an array of matches
//   - renderRow(it):  React node for each row in the dropdown
//   - renderSelected(it): node for the chip shown when a row is selected
//
// Optional:
//   - disabled
//   - noCreate (bool) — hide the "+ Add" row even when nothing matches
//   - addLabel(name)  — override the "+ Add 'X'" string

import React, { useEffect, useRef, useState } from 'react';

export default function EntityPicker({
  value,
  placeholder,
  onSelect,
  onCreate,
  search,
  renderRow,
  renderSelected,
  disabled = false,
  noCreate = false,
  addLabel = (n) => `+ Add "${n}"`,
}) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const boxRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  // Close on click outside.
  useEffect(() => {
    function onDown(e) {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await search(q);
        setItems(Array.isArray(results) ? results : []);
      } catch (e) {
        setError(e?.message || 'Search failed');
        setItems([]);
      } finally {
        setLoading(false);
      }
    }, 180);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [q, open, search]);

  // When the picker opens, fire an initial empty-query search so the
  // dropdown isn't blank. Most search endpoints return recent rows for q=''.
  useEffect(() => {
    if (open && items.length === 0 && q === '') {
      setLoading(true);
      search('').then((r) => setItems(Array.isArray(r) ? r : [])).catch(() => {}).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const showCreate = !noCreate && q.trim().length > 0 &&
    !items.some((it) => (it.name || it.full_name || '').toLowerCase() === q.trim().toLowerCase());

  async function handleCreate() {
    const name = q.trim();
    if (!name || !onCreate) return;
    setCreating(true);
    setError(null);
    try {
      const created = await onCreate(name);
      if (created) {
        onSelect && onSelect(created);
        setOpen(false);
        setQ('');
      }
    } catch (e) {
      setError(e?.message || 'Create failed');
    } finally {
      setCreating(false);
    }
  }

  // Selected chip view (when a row has been picked and we're not actively typing).
  if (value && !open) {
    return (
      <div ref={boxRef} className="relative">
        <button
          type="button"
          disabled={disabled}
          onClick={() => { setOpen(true); setQ(''); setTimeout(() => inputRef.current?.focus(), 0); }}
          className="w-full text-left bg-slate-900 border border-slate-600 hover:border-slate-500 rounded-lg px-3 py-2 flex items-center justify-between gap-2"
        >
          <div className="flex-1 min-w-0">{renderSelected(value)}</div>
          <span className="text-xs text-slate-500 flex-shrink-0">change</span>
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        disabled={disabled}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-slate-100 focus:ring-1 focus:ring-indigo-500 outline-none"
      />
      {open && (
        <div className="absolute z-50 left-0 right-0 mt-1 bg-slate-900 border border-slate-600 rounded-lg shadow-lg overflow-hidden max-h-72 overflow-y-auto scrollable-content">
          {loading && (
            <div className="px-3 py-2 text-xs text-slate-500">Searching…</div>
          )}
          {!loading && error && (
            <div className="px-3 py-2 text-xs text-red-400">{error}</div>
          )}
          {!loading && !error && items.map((it) => (
            <button
              key={it.id}
              type="button"
              onClick={() => { onSelect && onSelect(it); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-800 border-b border-slate-800 last:border-b-0"
            >
              {renderRow(it)}
            </button>
          ))}
          {!loading && !error && items.length === 0 && q.trim() === '' && (
            <div className="px-3 py-2 text-xs text-slate-500">Start typing to search…</div>
          )}
          {showCreate && (
            <button
              type="button"
              disabled={creating}
              onClick={handleCreate}
              className="w-full text-left px-3 py-2 bg-indigo-600/15 hover:bg-indigo-600/30 text-indigo-300 text-sm border-t border-indigo-700/40 flex items-center gap-2 disabled:opacity-60"
            >
              <span className="text-base leading-none">+</span>
              <span>{creating ? 'Creating…' : addLabel(q.trim())}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
